// apps/web/src/components/errors/ErrorPanel.tsx
//
// Reusable error display that consumes an ApiErrorEnvelope from the
// backend. Renders a coral-accented panel with:
//   - Title (always)
//   - Message (always)
//   - Suggestion (if present)
//   - "Show technical details" toggle (always — engineers want this)
//   - "Copy report" button → copies a markdown bug report to clipboard
//
// Drop this in anywhere an error needs to surface. Works inline at the
// top of a page, inside a card, or as the body of a toast.
//
// Usage:
//   import { ErrorPanel } from '../../components/errors/ErrorPanel';
//   import type { ApiErrorEnvelope } from '../../lib/api/types-errors';
//
//   {error && <ErrorPanel error={error.error} onRetry={handleRetry} />}

import { useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../engine/tokens';
import type { ApiError } from '../../lib/api/types-errors';

interface ErrorPanelProps {
  error:    ApiError;
  onRetry?: () => void;
  /** Compact variant — smaller padding, no expand toggle by default */
  compact?: boolean;
}

export function ErrorPanel({ error, onRetry, compact = false }: ErrorPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [copied,   setCopied]   = useState(false);

  const handleCopy = useCallback(async () => {
    const report = buildMarkdownReport(error);
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail in non-secure contexts. Fall back to
      // a textarea select-and-copy.
      const ta = document.createElement('textarea');
      ta.value = report;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); }
      finally { document.body.removeChild(ta); }
    }
  }, [error]);

  const hasTechnical =
    error.technical &&
    (error.technical.exceptionType ||
     error.technical.source ||
     (error.technical.stackFrames?.length ?? 0) > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      style={{
        ...panelStyle,
        padding: compact ? '14px 18px' : '20px 24px',
      }}
    >
      {/* ── Top edge accent ── */}
      <div style={topAccentStyle} />

      {/* ── Header row ── */}
      <div style={headerRowStyle}>
        <div style={iconBoxStyle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              stroke={C.coral}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={titleStyle}>{error.title}</h3>
          <code style={codeBadgeStyle}>{error.code}</code>
        </div>
        {onRetry && (
          <button onClick={onRetry} style={retryButtonStyle}>
            Try again
          </button>
        )}
      </div>

      {/* ── Message ── */}
      <p style={messageStyle}>{error.message}</p>

      {/* ── Suggestion ── */}
      {error.suggestion && (
        <div style={suggestionBoxStyle}>
          <span style={suggestionLabelStyle}>💡 SUGGESTION</span>
          <p style={suggestionTextStyle}>{error.suggestion}</p>
        </div>
      )}

      {/* ── Technical details toggle ── */}
      {hasTechnical && (
        <button onClick={() => setExpanded(e => !e)} style={toggleButtonStyle}>
          <span style={toggleArrowStyle(expanded)}>▸</span>
          {expanded ? 'Hide technical details' : 'Show technical details'}
        </button>
      )}

      {/* ── Technical details body ── */}
      {hasTechnical && expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.18 }}
          style={technicalBoxStyle}
        >
          {error.technical?.exceptionType && (
            <TechRow label="Type" value={error.technical.exceptionType} />
          )}
          {error.technical?.source && (
            <TechRow label="Source" value={error.technical.source} />
          )}
          {error.technical?.file && (
            <TechRow
              label="File"
              value={error.technical.line
                ? `${error.technical.file}:${error.technical.line}`
                : error.technical.file}
            />
          )}
          {(error.technical?.stackFrames?.length ?? 0) > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={techLabelStyle}>Stack:</div>
              <div style={stackBoxStyle}>
                {error.technical!.stackFrames.map((frame, i) => (
                  <div key={i} style={stackFrameStyle}>{frame}</div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* ── Footer: correlation id + copy button ── */}
      <div style={footerStyle}>
        <code style={correlationIdStyle} title="Correlation ID">
          {error.correlationId}
        </code>
        <button onClick={handleCopy} style={copyButtonStyle}>
          {copied ? '✓ Copied' : '📋 Copy report'}
        </button>
      </div>
    </motion.div>
  );
}

// ─── Sub-component ──────────────────────────────────────────────────────────
function TechRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
      <span style={techLabelStyle}>{label}:</span>
      <code style={techValueStyle}>{value}</code>
    </div>
  );
}

// ─── Markdown report builder ────────────────────────────────────────────────
function buildMarkdownReport(error: ApiError): string {
  const lines: string[] = [];
  lines.push(`**Error:** ${error.title}`);
  lines.push(`**Code:** \`${error.code}\``);
  lines.push(`**When:** ${new Date(error.timestamp).toISOString()}`);
  lines.push(`**Correlation ID:** \`${error.correlationId}\``);
  lines.push('');
  lines.push(error.message);
  if (error.suggestion) {
    lines.push('');
    lines.push(`**Suggestion:** ${error.suggestion}`);
  }
  if (error.technical) {
    lines.push('');
    lines.push('**Technical:**');
    if (error.technical.exceptionType)
      lines.push(`- Type: \`${error.technical.exceptionType}\``);
    if (error.technical.source)
      lines.push(`- Source: \`${error.technical.source}\``);
    if (error.technical.file)
      lines.push(`- File: \`${error.technical.file}${error.technical.line ? `:${error.technical.line}` : ''}\``);
    if (error.technical.stackFrames?.length) {
      lines.push('- Stack:');
      for (const f of error.technical.stackFrames) lines.push(`  - \`${f}\``);
    }
  }
  return lines.join('\n');
}

// ─── Styles ─────────────────────────────────────────────────────────────────
const panelStyle: React.CSSProperties = {
  position:        'relative',
  background:      'rgba(20, 8, 12, 0.85)',
  border:          `1px solid rgba(249, 112, 102, 0.35)`,
  borderRadius:    14,
  backdropFilter:  'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  boxShadow: [
    '0 0 0 1px rgba(255,255,255,0.03) inset',
    '0 16px 40px -12px rgba(0,0,0,0.6)',
    '0 0 32px -12px rgba(249,112,102,0.18)',
  ].join(', '),
  overflow:        'hidden',
  fontFamily:      F.display,
  color:           C.text,
};

const topAccentStyle: React.CSSProperties = {
  position: 'absolute',
  top:      0,
  left:     20,
  right:    20,
  height:   1,
  background: `linear-gradient(90deg, transparent, ${C.coral}, transparent)`,
  pointerEvents: 'none',
};

const headerRowStyle: React.CSSProperties = {
  display:    'flex',
  gap:        14,
  alignItems: 'flex-start',
  marginBottom: 10,
};

const iconBoxStyle: React.CSSProperties = {
  flexShrink:    0,
  width:         32,
  height:        32,
  borderRadius:  8,
  background:    'rgba(249,112,102,0.12)',
  border:        '1px solid rgba(249,112,102,0.3)',
  display:       'flex',
  alignItems:    'center',
  justifyContent: 'center',
};

const titleStyle: React.CSSProperties = {
  margin:        0,
  fontSize:      15,
  fontWeight:    600,
  color:         C.text,
  letterSpacing: '-0.005em',
  lineHeight:    1.3,
};

const codeBadgeStyle: React.CSSProperties = {
  display:        'inline-block',
  marginTop:      4,
  fontSize:       10.5,
  fontFamily:     F.mono,
  color:          'rgba(249,112,102,0.85)',
  background:     'rgba(249,112,102,0.08)',
  border:         '1px solid rgba(249,112,102,0.2)',
  padding:        '1px 6px',
  borderRadius:   3,
  letterSpacing:  '0.02em',
};

const retryButtonStyle: React.CSSProperties = {
  flexShrink:     0,
  fontSize:       12,
  fontFamily:     F.mono,
  letterSpacing:  '0.04em',
  textTransform:  'uppercase',
  color:          C.coral,
  background:     'rgba(249,112,102,0.08)',
  border:         '1px solid rgba(249,112,102,0.35)',
  padding:        '6px 12px',
  borderRadius:   6,
  cursor:         'pointer',
  transition:     'all 0.18s ease',
};

const messageStyle: React.CSSProperties = {
  margin:     '0 0 14px 46px',
  fontSize:   13.5,
  lineHeight: 1.55,
  color:      C.textSoft,
};

const suggestionBoxStyle: React.CSSProperties = {
  marginLeft:    46,
  marginBottom:  14,
  padding:       '12px 14px',
  background:    'rgba(34,211,238,0.05)',
  border:        '1px solid rgba(34,211,238,0.2)',
  borderRadius:  8,
};

const suggestionLabelStyle: React.CSSProperties = {
  display:       'block',
  fontSize:      10,
  fontFamily:    F.mono,
  letterSpacing: '0.08em',
  color:         C.cyan,
  marginBottom:  4,
};

const suggestionTextStyle: React.CSSProperties = {
  margin:     0,
  fontSize:   13,
  lineHeight: 1.5,
  color:      C.textSoft,
};

const toggleButtonStyle: React.CSSProperties = {
  display:       'flex',
  alignItems:    'center',
  gap:           6,
  marginLeft:    46,
  fontSize:      12,
  fontFamily:    F.mono,
  letterSpacing: '0.02em',
  color:         C.muted,
  background:    'transparent',
  border:        'none',
  padding:       '4px 0',
  cursor:        'pointer',
  transition:    'color 0.18s ease',
};

const toggleArrowStyle = (expanded: boolean): React.CSSProperties => ({
  display:    'inline-block',
  transform:  expanded ? 'rotate(90deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s ease',
});

const technicalBoxStyle: React.CSSProperties = {
  marginLeft:    46,
  marginTop:     10,
  marginBottom:  10,
  padding:       '14px 16px',
  background:    'rgba(0,0,0,0.35)',
  border:        '1px solid rgba(255,255,255,0.06)',
  borderRadius:  8,
  fontSize:      12,
  fontFamily:    F.mono,
  overflow:      'hidden',
};

const techLabelStyle: React.CSSProperties = {
  flexShrink: 0,
  width:      52,
  fontSize:   11,
  color:      C.dim,
  letterSpacing: '0.04em',
};

const techValueStyle: React.CSSProperties = {
  flex:       1,
  minWidth:   0,
  color:      C.textSoft,
  wordBreak:  'break-all',
};

const stackBoxStyle: React.CSSProperties = {
  marginTop:     6,
  paddingLeft:   12,
  borderLeft:    `2px solid rgba(249,112,102,0.25)`,
};

const stackFrameStyle: React.CSSProperties = {
  fontSize:   11.5,
  color:      C.muted,
  padding:    '2px 0',
  wordBreak:  'break-all',
};

const footerStyle: React.CSSProperties = {
  display:        'flex',
  justifyContent: 'space-between',
  alignItems:     'center',
  gap:            12,
  marginTop:      12,
  paddingTop:     12,
  marginLeft:     46,
  borderTop:      '1px solid rgba(255,255,255,0.06)',
};

const correlationIdStyle: React.CSSProperties = {
  fontSize:       11,
  fontFamily:     F.mono,
  color:          C.dim,
  letterSpacing:  '0.02em',
  overflow:       'hidden',
  textOverflow:   'ellipsis',
  whiteSpace:     'nowrap',
};

const copyButtonStyle: React.CSSProperties = {
  flexShrink:     0,
  fontSize:       11,
  fontFamily:     F.mono,
  letterSpacing:  '0.04em',
  textTransform:  'uppercase',
  color:          C.textSoft,
  background:     'rgba(255,255,255,0.04)',
  border:         '1px solid rgba(255,255,255,0.12)',
  padding:        '5px 10px',
  borderRadius:   5,
  cursor:         'pointer',
  transition:     'all 0.18s ease',
};
